import { useEffect, useRef, useState } from "react";

/**
 * Custom hook that uses Intersection Observer to detect when an element
 * is near the viewport. Useful for lazy loading sections and deferring
 * data fetching until the user scrolls close to them.
 *
 * @param rootMargin - How far before the element enters the viewport to trigger (default: 200px)
 * @param threshold  - Percentage of visibility needed to trigger (default: 0)
 * @returns [ref, isVisible] – ref to attach to the element, and whether it's been seen
 */
export const useLazyLoad = (
  rootMargin = "200px",
  threshold = 0
): [React.RefObject<HTMLElement | null>, boolean] => {
  const ref = useRef<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || isVisible) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [rootMargin, threshold, isVisible]);

  return [ref, isVisible];
};
